import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { setTenantId } from './tenantSlice';
import { authApi, AuthResponse } from '../api/authApi';

interface AuthState {
  token: string | null;
  user: AuthResponse['user'] | null;
  subdomain: string | null;
  preferredCatalogSlug: string | null;
}

interface CredentialsPayload {
  token: string;
  user: AuthResponse['user'];
  refreshToken?: string;
}

const initialState: AuthState = {
  token: typeof window !== 'undefined' ? window.localStorage.getItem('token') : null,
  user: typeof window !== 'undefined' ? (() => {
    try {
      const storedUser = window.localStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      return null;
    }
  })() : null,
  subdomain: typeof window !== 'undefined' ? window.localStorage.getItem('subdomain') : null,
  preferredCatalogSlug: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setToken(state, action: PayloadAction<string | null>) {
      console.log('[AUTH][SLICE] setToken called:', {
        hasToken: !!action.payload,
        tokenLength: action.payload?.length,
        tokenPreview: action.payload ? `${action.payload.substring(0, 20)}...` : 'null'
      });
      state.token = action.payload;
      if (typeof window !== 'undefined') {
        if (action.payload) {
          console.log('[AUTH][SLICE] Storing token in localStorage and cookie');
          window.localStorage.setItem('token', action.payload);
          // mirror cookie so SSR can read it
          document.cookie = `token=${encodeURIComponent(action.payload)}; Path=/; SameSite=Lax; Max-Age=86400`;
        } else {
          console.log('[AUTH][SLICE] Removing token from localStorage and cookie');
          window.localStorage.removeItem('token');
          document.cookie = 'token=; Path=/; Max-Age=0';
        }
      }
    },
    setUser(state, action: PayloadAction<AuthResponse['user'] | null>) {
      console.log('[AUTH][SLICE] setUser called:', {
        hasUser: !!action.payload,
        userId: action.payload?.id,
        userEmail: action.payload?.email,
        userRole: action.payload?.role
      });
      state.user = action.payload;
      if (typeof window !== 'undefined') {
        try {
          if (action.payload) {
            console.log('[AUTH][SLICE] Storing user in localStorage');
            window.localStorage.setItem('user', JSON.stringify(action.payload));
          } else {
            console.log('[AUTH][SLICE] Removing user from localStorage');
            window.localStorage.removeItem('user');
          }
        } catch {}
      }
    },
    setCredentials(state, action: PayloadAction<CredentialsPayload>) {
      state.token = action.payload.token;
      state.user = action.payload.user;
      if (typeof window !== 'undefined') {
        state.subdomain = window.localStorage.getItem('subdomain');
        window.localStorage.setItem('token', action.payload.token);
        if (action.payload.refreshToken) {
          window.localStorage.setItem('refreshToken', action.payload.refreshToken);
        }
        try { window.localStorage.setItem('user', JSON.stringify(action.payload.user || {})); } catch {}
        // cookie for SSR guard
        document.cookie = `token=${encodeURIComponent(action.payload.token)}; Path=/; SameSite=Lax; Max-Age=86400`;

        if (action.payload.user?.tenantId) {
          window.localStorage.setItem('tenantId', action.payload.user.tenantId);
        }
      }
    },
    logout(state) {
      console.log('[AUTH][SLICE] logout called - clearing all auth data');
      state.token = null;
      state.user = null;
      state.subdomain = null;
      state.preferredCatalogSlug = null;
      if (typeof window !== 'undefined') {
        console.log('[AUTH][SLICE] Removing all auth data from localStorage and cookies');
        window.localStorage.removeItem('token');
        window.localStorage.removeItem('user');
        window.localStorage.removeItem('tenantId');
        window.localStorage.removeItem('refreshToken');
        document.cookie = 'token=; Path=/; Max-Age=0';
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(authApi.endpoints.login.matchFulfilled, (state, { payload }) => {
        state.token = payload.token;
        state.user = payload.user;
        state.preferredCatalogSlug = payload.user?.preferred_catalog_slug || null;
        if (typeof window !== 'undefined') {
          state.subdomain = window.localStorage.getItem('subdomain');
          window.localStorage.setItem('token', payload.token);
          if (payload.tokens?.refreshToken) {
            window.localStorage.setItem('refreshToken', payload.tokens.refreshToken);
          }
          if (payload.user?.tenantId) {
            window.localStorage.setItem('tenantId', payload.user.tenantId);
          }
        }
      })
      .addMatcher(authApi.endpoints.register.matchFulfilled, (state, { payload }) => {
        state.token = payload.token;
        state.user = payload.user;
        if (typeof window !== 'undefined') {
          state.subdomain = window.localStorage.getItem('subdomain');
          window.localStorage.setItem('token', payload.token);
          if (payload.tokens?.refreshToken) {
            window.localStorage.setItem('refreshToken', payload.tokens.refreshToken);
          }
          if (payload.user?.tenantId) {
            window.localStorage.setItem('tenantId', payload.user.tenantId);
          }
        }
      });
  },
});

export const { setCredentials, logout } = authSlice.actions;
export const { setToken, setUser } = authSlice.actions;
export default authSlice.reducer;
